'use client';

import { useState, useEffect } from 'react';

import ServiceCard from './ServiceCard';
import ComparisonTable from './ComparisonTable';
import TestimonialCard from './TestimonialCard';
import BookingWidget from './BookingWidget';

interface Amenity {
  icon: string;
  title: string;
  description: string;
  features: string[];
  image: string;
  alt: string;
}

interface Service {
  icon: string;
  title: string;
  description: string;
  availability: string;
}

interface ComparisonItem {
  feature: string;
  proximus: boolean | string;
  traditionalHotel: boolean | string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
}

export default function AmenitiesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const amenities: Amenity[] = [
  {
    icon: '🏋️',
    title: 'Fitness Center @ Skypool Gym',
    description: 'Maintain your fitness routine with our fully equipped gym featuring modern cardio and strength training equipment, just 1 minute walking distance from the property.',
    features: [
    'Latest cardio machines with entertainment screens',
    'Free weights and resistance training equipment',
    'Yoga mats and stretching area',
    'Personal training sessions available',
    'Open 24/7 for your convenience'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_171e2eee3-1767789047012.png",
    alt: 'Modern fitness center with treadmills, exercise bikes, and weight training equipment in bright spacious room'
  },
  {
    icon: '🏊',
    title: 'Swimming Pool @ Skypool',
    description: 'Relax and unwind in our temperature-controlled indoor swimming pool with modern facilities, just 1 minute walking distance from the property.',
    features: [
    'Temperature-controlled indoor pool',
    'Modern swimming facilities',
    'Poolside loungers and seating',
    'Pool towel service included',
    'Professional lifeguard on duty'],

    image: "https://images.unsplash.com/photo-1597745171477-047b4464438b",
    alt: 'Temperature-controlled indoor swimming pool with modern facilities and comfortable seating area'
  },
  {
    icon: '💼',
    title: 'Dedicated Workspace',
    description: 'Conduct business seamlessly with dedicated workspace in every apartment featuring high-speed internet and ergonomic setup.',
    features: [
    'High-speed fiber optic internet',
    'Ergonomic desk and chair',
    'Adequate lighting for productivity',
    'Power outlets and USB charging',
    'Quiet environment for focused work'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13c043a1c-1767677465552.png",
    alt: 'Professional workspace with modern desk, ergonomic chair, and high-speed internet connectivity'
  },
  {
    icon: '🍽️',
    title: 'Fully Equipped Kitchen',
    description: 'Enjoy the comfort of home with our modern, fully-equipped kitchens in every apartment.',
    features: [
    'Premium appliances including dishwasher',
    'Microwave, oven, and stovetop',
    'Complete cookware and utensils',
    'Refrigerator with ice maker',
    'Dining area with seating'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1112e2fe8-1769066954543.png",
    alt: 'Contemporary apartment kitchen with white cabinets, stainless steel appliances, marble countertops, and modern pendant lighting'
  },
  {
    icon: '🧺',
    title: 'Laundry Facilities',
    description: 'Convenient on-site laundry services to keep your wardrobe fresh throughout your stay.',
    features: [
    'In-unit washer and dryer in premium apartments',
    'Shared laundry room with modern machines',
    'Laundry detergent and supplies available',
    'Express laundry service option',
    'Dry cleaning pickup and delivery'],

    image: "https://images.unsplash.com/photo-1723641875452-38641e108a28",
    alt: 'Clean modern laundry room with front-loading washing machines, dryers, folding counter, and bright overhead lighting'
  },
  {
    icon: '🚗',
    title: 'Secure Parking & Transportation',
    description: 'Hassle-free parking and transportation services for your convenience.',
    features: [
    'Covered parking with 24/7 security',
    'Electric vehicle charging stations',
    'Airport shuttle service available',
    'Car rental assistance',
    'Bicycle storage and rentals'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d93fd3eb-1768267270117.png",
    alt: 'Underground parking garage with well-lit spaces, clear markings, security cameras, and modern ventilation system'
  }];


  const services: Service[] = [
  {
    icon: '🛎️',
    title: '24/7 Concierge Service',
    description: 'Our dedicated concierge team is available round-the-clock to assist with reservations, recommendations, and any special requests.',
    availability: 'Available 24/7'
  },
  {
    icon: '🧹',
    title: 'Daily Housekeeping',
    description: 'Professional housekeeping services to maintain your apartment in pristine condition throughout your stay.',
    availability: 'Daily at preferred time'
  },
  {
    icon: '🍳',
    title: 'Grocery Delivery Service',
    description: 'Pre-stock your kitchen or order groceries during your stay with our convenient delivery service.',
    availability: 'On-demand'
  },
  {
    icon: '🔐',
    title: 'Secure Package Reception',
    description: 'Receive and store packages securely with our 24-hour package handling service.',
    availability: 'Available 24/7'
  },
  {
    icon: '🌐',
    title: 'High-Speed WiFi',
    description: 'Complimentary fiber-optic internet throughout the property with dedicated bandwidth for each apartment.',
    availability: 'Included'
  },
  {
    icon: '👨‍🔧',
    title: 'Maintenance Support',
    description: 'Quick response maintenance team available for any technical issues or apartment needs.',
    availability: 'Available 24/7'
  }];


  const comparisonItems: ComparisonItem[] = [
  { feature: 'Full Kitchen', proximus: 'Yes', traditionalHotel: 'No' },
  { feature: 'Separate Living Area', proximus: 'Yes', traditionalHotel: 'No' },
  { feature: 'In-Unit Laundry', proximus: 'Shared Facilities', traditionalHotel: 'No' },
  { feature: 'Weekly Housekeeping', proximus: 'Yes', traditionalHotel: 'Daily only' },
  { feature: 'Workspace Area', proximus: 'Yes', traditionalHotel: 'Limited' },
  { feature: 'Grocery Delivery', proximus: 'Yes', traditionalHotel: 'No' },
  { feature: 'Pet-Friendly Options', proximus: 'Yes', traditionalHotel: 'Rare' },
  { feature: 'Long-Term Discounts', proximus: 'Yes', traditionalHotel: 'Minimal' },
  { feature: 'Fitness Center', proximus: 'Yes (Off-Premises)', traditionalHotel: 'Sometimes' },
  { feature: 'Swimming Pool', proximus: 'Yes (Off-Premises)', traditionalHotel: 'Sometimes' },
  { feature: 'Business Center', proximus: 'Workspace in Unit', traditionalHotel: 'Sometimes' },
  { feature: 'Parking Included', proximus: 'Yes', traditionalHotel: 'Yes' }];


  const testimonials: Testimonial[] = [
  {
    quote: 'The amenities at Proximus exceeded all expectations. The fitness center is better equipped than my gym at home, and having a full kitchen made my month-long stay feel like home.',
    author: 'Rajesh Kumar',
    role: 'Senior Consultant',
    company: 'McKinsey & Company',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12c863552-1763294002377.png",
    alt: 'Professional Indian man in navy blue suit with confident smile in modern office setting',
    rating: 5
  },
  {
    quote: 'The concierge service is outstanding. They helped arrange everything from grocery delivery to restaurant reservations. The attention to detail makes Proximus stand out from typical hotels.',
    author: 'Sarah Mitchell',
    role: 'VP of Operations',
    company: 'Amazon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3a1efe8-1772875264718.png",
    alt: 'Caucasian businesswoman with blonde hair in gray blazer smiling professionally in corporate environment',
    rating: 5
  },
  {
    quote: 'As a family, we loved having the pool and separate living spaces. The kids enjoyed the pool while we could work comfortably. It\'s the perfect blend of vacation and functionality.',
    author: 'Priya Sharma',
    role: 'Marketing Director',
    company: 'Google India',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe9b5695-1763296011297.png",
    alt: 'Indian woman with long dark hair in professional attire with warm smile in bright office space',
    rating: 5
  }];


  const categories = [
  { id: 'all', label: 'All Amenities', icon: '🏢' },
  { id: 'wellness', label: 'Wellness', icon: '💪' },
  { id: 'business', label: 'Business', icon: '💼' },
  { id: 'lifestyle', label: 'Lifestyle', icon: '🌟' }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse space-y-8 py-16">
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) =>
              <div key={i} className="h-96 bg-gray-200 rounded"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-primary text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-90"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 text-white">Services That Elevate Your Stay

            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Experience luxury that works as hard as you do. From state-of-the-art fitness facilities to 24/7 concierge service, every detail is designed for your comfort and convenience.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Services */}
            <div className="lg:col-span-2 space-y-16">
              {/* Services */}
              <div>
                <h2 className="font-display text-3xl font-bold text-text-primary mb-8">Concierge Services</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service, index) =>
                  <ServiceCard key={index} {...service} />
                  )}
                </div>
              </div>

              {/* Comparison Table */}
              <div>
                <h2 className="font-display text-3xl font-bold text-text-primary mb-4">Why Choose Proximus?</h2>
                <p className="text-text-secondary mb-8 leading-relaxed">
                  See how our serviced apartments compare to traditional hotels and understand the value of choosing Proximus for your stay.
                </p>
                <ComparisonTable items={comparisonItems} />
              </div>

              {/* Testimonials */}
              <div>
                <h2 className="font-display text-3xl font-bold text-text-primary mb-8">Guest Experiences</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {testimonials.map((testimonial, index) =>
                  <TestimonialCard key={index} {...testimonial} />
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Booking Widget */}
            <div className="lg:col-span-1">
              <BookingWidget />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary to-secondary text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-6 text-[rgba(252,252,252,1)]">
            Experience Luxury Living Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book your stay at Proximus and discover amenities that redefine serviced apartment living in Bangalore.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="px-8 py-4 bg-accent hover:bg-accent/90 text-white font-semibold rounded-lg shadow-elevated hover:shadow-premium transition-all duration-300 hover:scale-105">
              Book Your Stay
            </button>
          </div>
        </div>
      </section>
    </div>);

}